import React, { CSSProperties, forwardRef, ReactNode } from "react";
import {
    AutoSizer,
    CellMeasurer,
    CellMeasurerCache,
    CellMeasurerCacheParams,
    defaultCellRangeRenderer,
    GridCellRangeProps,
    GridCellRangeRenderer,
    IndexRange,
    List,
    ScrollSync,
} from "react-virtualized";

import { bemClassNames, EMPTY_ARRAY, useForceUpdate } from "@multiweb/reactUtils";
import { ForwardRefType } from "@multiweb/types/react";
import { logger } from "../../logger";
import { MenuScrollLayout } from "../MenuScrollLayout";

export type MenuVirtualizedListItemRenderer<T> = (item: T, index: number, items: ReadonlyArray<T>) => ReactNode;
export type MenuVirtualizedListKeyGenerator<T> = (item: T, index: number, items: ReadonlyArray<T>) => string;

export type MenuVirtualizedListProps<T> = {
    /** Data pro vykreslení položek. */
    items: ReadonlyArray<T>;
    /** Funkce pro generování klíčů položek. */
    keyGenerator: MenuVirtualizedListKeyGenerator<T>;
    /** Funkce pro vykreslení položek. */
    itemRenderer: MenuVirtualizedListItemRenderer<T>;
    /** Parametry pro cache rozměrů položek. */
    cellMeasureCacheParams: CellMeasurerCacheParams;
    /** Obsah hlavičky. */
    stickyHeaderContent?: ReactNode;
    /** Obsah patičky. */
    stickyFooterContent?: ReactNode;
    /** Viz MenuScrollLayout usePaddedScrollbars.*/
    usePaddedScrollbars?: boolean;
    /** Funkce volaná po vykreslení řádků - interní využití */
    onRowsRendered?: (indexRange: IndexRange) => void;
    /** Index zaměřené položky. Zaměřená položka je vždy vykreslená, nehledě na polohu scrollbaru. To umožňuje prohlížeči scrollovat na zaměřenou
     * položku. Například při navigaci z první položky na poslední. */
    focusedItemIndex?: number;
};

const STICKY_FOOTER_KEY = "menu-virtualized-list-sticky-footer";
const classNames = bemClassNames("menu-virtualized-list");

export const MenuVirtualizedList: ForwardRefType<List, MenuVirtualizedListProps<any>> = forwardRef<List, MenuVirtualizedListProps<any>>(({
    items,
    itemRenderer,
    keyGenerator,
    cellMeasureCacheParams,
    stickyHeaderContent,
    stickyFooterContent,
    usePaddedScrollbars = true,
    onRowsRendered,
    focusedItemIndex,
}, ref) => {
    /* Cache má jako klíče indexy, proto se musí vytvořit znovu při změně dat, jinak by používala výšky nesprávných položek.
    Cache slouží pro jednu sadu položek při jejich scrollování. */
    const cache = new CellMeasurerCache(cellMeasureCacheParams);
    const rowCount = items.length + (stickyFooterContent ? 1 : 0);
    /* Po přidání paddingu v MenuScrollLayout je nutné překreslit virtualizovaný list pro správné zobrazení položek.
    Tím se opraví překrývání položek s dlouhým textem. */
    const forceUpdate = useForceUpdate();

    return (
        <div className={classNames()} >
            <ScrollSync>
                {({ clientHeight, clientWidth, onScroll, scrollHeight, scrollLeft, scrollTop, scrollWidth }) => (
                    <MenuScrollLayout
                        onContentSizeChanged={usePaddedScrollbars ? forceUpdate : undefined}
                        onScroll={(scrollEvent) => {
                            if (scrollEvent && scrollEvent.target) {
                                const scrollTop = (scrollEvent.target as HTMLElement).scrollTop;
                                onScroll({ scrollTop, clientHeight, clientWidth, scrollHeight, scrollLeft, scrollWidth });
                            } else {
                                logger.warn("Cannot get scrollTop property from scroll event.");
                            }
                        }}
                        inVirtualizedList={true}
                        {...{ stickyHeaderContent, stickyFooterContent, usePaddedScrollbars }}
                    >
                        <AutoSizer>
                            {({ height, width }) => {
                                return (
                                    <List
                                        tabIndex={null}
                                        rowHeight={cache.rowHeight}
                                        deferredMeasurementCache={cache}
                                        className={classNames("list")}
                                        cellRangeRenderer={createCellRangeRenderer(focusedItemIndex, items.length, !!stickyFooterContent)}
                                        {...{ rowCount, width, height, scrollTop, style, onRowsRendered, ref }}
                                        rowRenderer={({ index, style, parent }) => {
                                            const isFooter = stickyFooterContent && index === items.length;
                                            const key = isFooter ? STICKY_FOOTER_KEY : keyGenerator(items[index], index, items);
                                            return (
                                                <CellMeasurer
                                                    key={key}
                                                    cache={cache}
                                                    parent={parent}
                                                    index={index}
                                                >
                                                    <div style={style} className={isFooter ? classNames("inner-sticky-footer") : undefined}>
                                                        {isFooter ? (
                                                            stickyFooterContent
                                                        ) : (
                                                            itemRenderer(items[index], index, items)
                                                        )}
                                                    </div>
                                                </CellMeasurer>
                                            );
                                        }}
                                    />
                                );
                            }}
                        </AutoSizer>
                    </MenuScrollLayout>
                )}
            </ScrollSync>
        </div>
    );
});

/**
 * Nastaví inline styly pro zajištění kompatibility s custom scrollbar. Hodnoty se musí nastavit pomocí inline stylů, protože je tak nastavuje Grid komponenta, kterou
 * používá List. Inline styly není možné přepsat z css bez použití !important. Proto není toto stylování zajištěno přes css a BEM.
 *
 * Nastavení overflow skryje scrollbar listu, tím že je přesah viditelný. Musí být zadáno zvlášť pro X a Y, aby se správně přepsaly výchozí hodnoty v Grid komponentě.
 */
const style: CSSProperties = {
    overflowX: "visible",
    overflowY: "visible",
};

/**
 * Vytvoří vykreslovací funkci, které ve virtualizovaném listu vždy vykreslí zaměřený prvek, nezávisle na poloze scrollbaru. Upravuje pouze to, jaké položky se
 * vykreslují. Vykreslení samotné zajišťuje výchozí vykreslovací funkce defaultCellRangeRenderer z knihovny react-virtualized.
 *
 * Vykreslení zaměřeného prvku je důležité pro správnou funkci navigace přes klávesnici. Umožňuje zascrollovat na zaměřený prvek, i když je mimo viditelnou oblast.
 * @param focusedItemIndex Index zaměřeného prvku v listu. Pokud není zadaný, použije se výchozí vykreslování.
 * @param itemsCount Celkový počet prvků (patička se nezapočítává).
 * @param hasFooter Příznak, zda list zobrazuje patičku.
 */
const createCellRangeRenderer: (focusedItemIndex: number | undefined, itemsCount: number, hasFooter: boolean) => GridCellRangeRenderer =
    (focusedItemIndex, itemsCount, hasFooter) => {
        if (focusedItemIndex === undefined) {
            return defaultCellRangeRenderer;
        }

        return (params) => {
            const previousItems = renderPreviousItems(params, focusedItemIndex, defaultCellRangeRenderer);
            const focusedItems = renderFocusedItem(params, focusedItemIndex, defaultCellRangeRenderer);
            const nextItems = renderNextItems(params, focusedItemIndex, itemsCount, defaultCellRangeRenderer);
            const footer = renderFooter(hasFooter, params, itemsCount, defaultCellRangeRenderer);
            return [...previousItems, ...focusedItems, ...nextItems, ...footer];
        };
    };

type RenderFunction = typeof defaultCellRangeRenderer;

export const renderPreviousItems = (params: GridCellRangeProps, focusedItemIndex: number, renderFunction: RenderFunction) => {
    const { rowStartIndex, rowStopIndex } = params;
    if (focusedItemIndex > rowStartIndex) {
        const previousItemsParams = {
            ...params,
            rowStopIndex: Math.min(rowStopIndex, focusedItemIndex - 1),
        };
        return renderFunction(previousItemsParams);
    } else {
        return EMPTY_ARRAY;
    }
};

export const renderFocusedItem = (params: GridCellRangeProps, focusedItemIndex: number, renderFunction: RenderFunction) => {
    const focusedItemParams = {
        ...params,
        rowStartIndex: focusedItemIndex,
        rowStopIndex: focusedItemIndex,
    };
    return renderFunction(focusedItemParams);
};

export const renderNextItems = (params: GridCellRangeProps, focusedItemIndex: number, itemsCount: number, renderFunction: RenderFunction) => {
    const { rowStartIndex, rowStopIndex } = params;
    if (rowStopIndex > focusedItemIndex && focusedItemIndex < itemsCount - 1) {
        const nextItemsParams = {
            ...params,
            rowStartIndex: Math.max(rowStartIndex, focusedItemIndex + 1),
            rowStopIndex: Math.min(Math.max(rowStopIndex, focusedItemIndex + 1), itemsCount - 1),
        };
        return renderFunction(nextItemsParams);
    } else {
        return EMPTY_ARRAY;
    }
};

export const renderFooter = (hasFooter: boolean, params: GridCellRangeProps, itemsCount: number, renderFunction: RenderFunction) => {
    const { rowStopIndex } = params;
    if (hasFooter && rowStopIndex === itemsCount) {
        const footerParams = {
            ...params,
            rowStartIndex: itemsCount,
            rowStopIndex: itemsCount,
        };
        return renderFunction(footerParams);
    } else {
        return EMPTY_ARRAY;
    }
};
